import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { PanelModule } from 'primeng/panel';
import { ClientTemplateService } from '../../services/client-template.service';
import { InvoiceData } from '../../services/pdf-generation/models/invoice-data.interface';
import { PdfGenerationService } from '../../services/pdf-generation/pdf-generation.service';

@Component({
  selector: 'app-pdf-template-tester',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DropdownModule,
    CardModule,
    PanelModule
  ],
  templateUrl: './pdf-template-tester.component.html',
  styleUrl: './pdf-template-tester.component.scss'
})
export class PdfTemplateTesterComponent implements OnInit {
  availableTemplates: { label: string; value: string }[] = [];
  selectedTemplate: string = 'default';

  // Sample invoice data for testing
  sampleInvoiceData: InvoiceData = {
    invoiceNumber: 'INV-2025-001',
    invoiceDate: new Date(),
    createdAt: new Date().toISOString(),
    customerName: 'John Doe',
    customerMobile: '9876543210',
    customerAddress: {
      line1: '123, Sample Street',
      line2: 'Near City Mall',
      line3: 'Thoraipakkam',
      city: 'Chennai',
      state: 'Tamil Nadu',
      country: 'India',
      zipCode: '600097'
    },
    items: [
      {
        name: 'Product A - Premium Quality',
        sku: 'SKU001',
        hsn: '1234',
        batch: 'BATCH001',
        brand: 'Brand X',
        quantity: 2,
        price: 500,
        discount: 50,
        discountPercentage: 10,
        tax: 18,
        cgst: 9,
        sgst: 9,
        total: 900,
        netValue: 1062
      },
      {
        name: 'Product B - Standard',
        sku: 'SKU002',
        hsn: '5678',
        batch: 'BATCH002',
        brand: 'Brand Y',
        quantity: 1,
        price: 1000,
        discount: 0,
        tax: 18,
        cgst: 9,
        sgst: 9,
        total: 1000,
        netValue: 1180
      },
      {
        name: 'Product C - Economy Pack',
        sku: 'SKU003',
        hsn: '9012',
        batch: 'BATCH003',
        brand: 'Brand Z',
        quantity: 5,
        price: 200,
        discount: 100,
        discountPercentage: 10,
        tax: 12,
        cgst: 6,
        sgst: 6,
        total: 900,
        netValue: 1008
      }
    ],
    subtotal: 2800,
    discount: 150,
    discountPercentage: 5,
    taxAmount: 477,
    cgst: 238.5,
    sgst: 238.5,
    totalAmount: 3127,
    paymentMethod: 'Cash',
    paidamount: 3127,
    balanceamount: 0,
    cashAmount: 3127,
    businessName: 'ElixirPOS Demo Store',
    businessAddress: '456, Business Park, Anna Nagar, Chennai - 600040',
    businessPhone: '+91 98765 43210',
    businessEmail: 'info@elixirpos.com',
    businessGSTIN: '33AABCU9603R1ZM',
    invoiceType: 'sale',
    notes: 'Thank you for your business! Visit again.',
    terms: 'Goods once sold will not be taken back. All disputes subject to Chennai jurisdiction.'
  };

  // Sample quotation data
  sampleQuotationData: InvoiceData = {
    ...this.sampleInvoiceData,
    invoiceNumber: 'QUO-2025-001',
    invoiceType: 'quotation',
    quotationValidityDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
    paidamount: 0,
    balanceamount: 3127,
    notes: 'This is a quotation. Not a tax invoice.',
    terms: 'Quotation valid for 7 days. Prices subject to change without notice.'
  };

  constructor(
    private pdfService: PdfGenerationService,
    private clientTemplateService: ClientTemplateService
  ) { }

  ngOnInit() {
    this.loadAvailableTemplates();
    this.loadClientTemplates();
  }

  loadClientTemplates() {
    // Load client templates for testing (use client_id from localStorage)
    const clientIdStr = localStorage.getItem('client_id');
    if (clientIdStr) {
      const clientId = JSON.parse(clientIdStr);
      this.clientTemplateService.getClientTemplates(clientId).subscribe({
        next: (templates) => {
          console.log('Client templates loaded:', templates);
          const defaultTemplate = this.clientTemplateService.getDefaultTemplate();
          if (defaultTemplate) {
            console.log('Default template:', defaultTemplate);
            this.selectedTemplate = this.clientTemplateService.getTemplateNameById(defaultTemplate.template_id);
          }
        },
        error: (error) => {
          console.error('Error loading client templates:', error);
        }
      });
    }
  }

  loadAvailableTemplates() {
    const templates = this.pdfService.getAvailableTemplates();
    this.availableTemplates = templates.map(template => {
      const info = this.pdfService.getTemplateInfo(template);
      return {
        label: `${template} (${info?.pageSize || 'Unknown'})`,
        value: template
      };
    });
  }

  generateInvoice() {
    console.log(`Generating invoice with template: ${this.selectedTemplate}`);
    this.pdfService.generateInvoicePdf(this.sampleInvoiceData, this.selectedTemplate);
  }

  generateQuotation() {
    console.log(`Generating quotation with template: ${this.selectedTemplate}`);
    this.pdfService.generateInvoicePdf(this.sampleQuotationData, this.selectedTemplate);
  }

  openInvoice() {
    console.log(`Opening invoice with template: ${this.selectedTemplate}`);
    this.pdfService.openInvoicePdf(this.sampleInvoiceData, this.selectedTemplate);
  }

  openQuotation() {
    console.log(`Opening quotation with template: ${this.selectedTemplate}`);
    this.pdfService.openInvoicePdf(this.sampleQuotationData, this.selectedTemplate);
  }

  printInvoice() {
    console.log(`Printing invoice with template: ${this.selectedTemplate}`);
    this.pdfService.printInvoicePdf(this.sampleInvoiceData, this.selectedTemplate);
  }

  printQuotation() {
    console.log(`Printing quotation with template: ${this.selectedTemplate}`);
    this.pdfService.printInvoicePdf(this.sampleQuotationData, this.selectedTemplate);
  }

  testAllTemplates() {
    console.log('Testing all templates...');
    this.availableTemplates.forEach((template, index) => {
      setTimeout(() => {
        console.log(`Generating PDF for template: ${template.value}`);
        this.pdfService.generateInvoicePdf(
          this.sampleInvoiceData,
          template.value,
          `test_${template.value}_invoice.pdf`
        );
      }, index * 1000); // 1 second delay between each
    });
  }

  getTemplateInfo() {
    const info = this.pdfService.getTemplateInfo(this.selectedTemplate);
    if (info) {
      alert(`Template: ${info.name}\nVersion: ${info.version}\nPage Size: ${info.pageSize}\nDescription: ${info.description}`);
    }
  }
}

